import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowTopRightOnSquareIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';
import moment from 'moment';

/**
 * Panel to manage Asaas charges for a company
 */
export function AsaasChargesPanel({ company, isOpen, onClose }) {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    dueDate: '',
    description: '',
    billingType: 'BOLETO',
  });

  const loadCharges = useCallback(async () => {
    if (!company?._database?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/asaas/charges/${company.id}`,
        { params: { database: company._database.id !== 'consolidated' ? company._database.id : undefined } }
      );
      setCharges(data.data || []);
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      console.error('Failed to load Asaas charges:', message);
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => {
    if (isOpen && company) {
      loadCharges();
    }
  }, [isOpen, company, loadCharges]);

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
      RECEIVED: { label: 'Recebido', color: 'bg-green-100 text-green-800' },
      RECEIVED_IN_CASH: { label: 'Recebido em Dinheiro', color: 'bg-green-100 text-green-800' },
      OVERDUE: { label: 'Vencido', color: 'bg-red-100 text-red-800' },
      REFUNDED: { label: 'Estornado', color: 'bg-gray-100 text-gray-800' },
    };
    
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const handleCreateCharge = async () => {
    if (!formData.value) {
      toast.error('Valor é obrigatório');
      return;
    }
    
    try {
      await api.post(`/asaas/charges/${company.id}`, {
        ...formData,
        database: company._database.id,
      });
      toast.success('Cobrança criada com sucesso');
      setShowCreateModal(false);
      setFormData({
        value: '',
        dueDate: '',
        description: '',
        billingType: 'BOLETO',
      });
      await loadCharges();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao criar cobrança');
    }
  };

  const handleUpdateCharge = async () => {
    if (!selectedCharge) return;
    
    try {
      await api.put(`/asaas/charges/${selectedCharge.id}`, {
        chargeData: formData,
      });
      toast.success('Cobrança atualizada com sucesso');
      setShowEditModal(false);
      setSelectedCharge(null);
      await loadCharges();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao atualizar cobrança');
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta cobrança?')) return;
    
    try {
      await api.delete(`/asaas/charges/${chargeId}`);
      toast.success('Cobrança deletada com sucesso');
      await loadCharges();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao deletar cobrança');
    }
  };

  const openEditModal = (charge) => {
    setSelectedCharge(charge);
    setFormData({
      value: charge.value,
      dueDate: charge.dueDate,
      description: charge.description || '',
      billingType: charge.billingType,
    });
    setShowEditModal(true);
  };

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[65vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="text-3xl">💳</div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Cobranças Asaas</h2>
              <p className="text-base text-gray-600">{company.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              <p className="font-semibold text-base">Erro ao carregar cobranças</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-base text-gray-500">Carregando cobranças...</p>
              </div>
            </div>
          ) : charges.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-base text-gray-500">Nenhuma cobrança encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-4 text-left font-semibold text-gray-900">ID</th>
                    <th className="px-6 py-4 text-left font-semibold text-gray-900">Valor</th>
                    <th className="px-6 py-4 text-left font-semibold text-gray-900">Vencimento</th>
                    <th className="px-6 py-4 text-left font-semibold text-gray-900">Status</th>
                    <th className="px-6 py-4 text-center font-semibold text-gray-900">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((charge, idx) => (
                    <tr
                      key={charge.id}
                      className={`transition ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } hover:bg-blue-50 border-b border-gray-100`}
                    >
                      <td className="px-6 py-4 font-mono text-sm text-gray-600">
                        {charge.id?.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 font-semibold text-base text-gray-900">
                        R$ {(charge.value || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-base text-gray-600">
                        {charge.dueDate ? moment(charge.dueDate).format('DD/MM/YYYY') : '-'}
                      </td>
                      <td className="px-6 py-4 text-base">
                        {(() => {
                          const badge = getStatusBadge(charge.status);
                          return (
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>
                              {badge.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => openEditModal(charge)}
                            className="inline-flex items-center gap-1 px-3 py-2 text-base text-blue-600 hover:bg-blue-50 rounded transition"
                            title="Editar cobrança"
                          >
                            <PencilIcon className="w-5 h-5" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteCharge(charge.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 text-base text-red-600 hover:bg-red-50 rounded transition"
                            title="Deletar cobrança"
                          >
                            <TrashIcon className="w-5 h-5" />
                            Deletar
                          </button>
                          <a
                            href={charge.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-2 text-base text-green-600 hover:bg-green-50 rounded transition"
                            title="Abrir no Asaas"
                          >
                            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                            Ver
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-between p-6 border-t border-gray-200 sticky bottom-0 bg-gray-50">
          <div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-5 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition flex items-center gap-2 text-base font-semibold"
            >
              <PlusIcon className="w-5 h-5" />
              Nova Cobrança
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
            >
              Fechar
            </button>
            <a
              href="https://app.asaas.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2 text-base font-semibold"
            >
              <ArrowTopRightOnSquareIcon className="w-5 h-5" />
              Dashboard Asaas
            </a>
          </div>
        </div>

        {/* Create Charge Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <PlusIcon className="w-6 h-6" />
                Nova Cobrança
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: parseFloat(e.target.value) || ''})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data de Vencimento</label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="Descrição da cobrança"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Cobrança</label>
                  <select
                    value={formData.billingType}
                    onChange={(e) => setFormData({...formData, billingType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  >
                    <option value="BOLETO">Boleto</option>
                    <option value="CREDIT_CARD">Cartão de Crédito</option>
                    <option value="PIX">PIX</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition text-base"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateCharge}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-base"
                >
                  Criar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Charge Modal */}
        {showEditModal && selectedCharge && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <PencilIcon className="w-6 h-6" />
                Editar Cobrança
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: parseFloat(e.target.value) || ''})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data de Vencimento</label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="Descrição da cobrança"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Cobrança</label>
                  <select
                    value={formData.billingType}
                    onChange={(e) => setFormData({...formData, billingType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  >
                    <option value="BOLETO">Boleto</option>
                    <option value="CREDIT_CARD">Cartão de Crédito</option>
                    <option value="PIX">PIX</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCharge(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition text-base"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdateCharge}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-base"
                >
                  Atualizar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
